import { QueryResult } from '@apollo/client';
import {
  DocumentForRenewalQuery,
  DocumentForRenewalQueryVariables,
  DocumentRenewMutationVariables,
  DocumentTemplatesQuery,
  DocumentTemplatesQueryVariables,
  useDocumentForRenewalQuery,
  useDocumentRenewMutation,
  useDocumentTemplatesQuery,
} from 'generated/graphql';
import moment from 'moment';
import { ReactEventHandler } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

interface FormData {
  nonExpiring: boolean;
  expiresOn: string;
  eSign: boolean;
  file?: FileList;
  templateId?: string;
}

const getFormData = ({ document }: DocumentForRenewalQuery): FormData => ({
  nonExpiring: document.nonExpiring,
  expiresOn: document.expiresOn && moment(document.expiresOn).add(1, 'year').format('Y-MM-DD'),
  eSign: document.esign,
});

const getMutationData = (data: FormData): DocumentRenewMutationVariables['data'] => ({
  esign: data.eSign,
  ...(data.eSign && { esignTemplateId: data.templateId }),
  ...(!data.eSign && {
    expiresOn: data.nonExpiring ? null : data.expiresOn,
    nonExpiring: data.nonExpiring,
    file: data.file![0],
  }),
});

export const useDocumentRenewalForm = (): {
  document: QueryResult<DocumentForRenewalQuery, DocumentForRenewalQueryVariables>;
  documentTemplates: QueryResult<DocumentTemplatesQuery, DocumentTemplatesQueryVariables>;
  form: UseFormReturn<FormData, any>;
  onSubmit: ReactEventHandler;
  loading: boolean;
  initialLoadDone: boolean;
} => {
  const form = useForm<FormData>();
  const { documentId: id } = useParams() as { documentId: string };
  const navigate = useNavigate();
  const goBack = () => navigate(-1);

  const document = useDocumentForRenewalQuery({
    variables: { id },
    onCompleted: data => form.reset(getFormData(data)),
  });

  const documentTemplates = useDocumentTemplatesQuery();

  const [renewDocument, { loading }] = useDocumentRenewMutation({ onCompleted: goBack });

  const onSubmit = form.handleSubmit(values =>
    renewDocument({ variables: { id, data: getMutationData(values) } })
  );

  return {
    document,
    documentTemplates,
    form,
    onSubmit,
    loading: document.loading || documentTemplates.loading || loading,
    // workaround for chakra checkboxes not rerendering when form is reset
    // this will be used to only render the form after the after the query when the state has been reset
    initialLoadDone: !document.loading && !documentTemplates.loading,
  };
};
